import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import toast from 'react-hot-toast';

const UserContext = createContext();

export const useUserDetails = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserDetails must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userExists, setUserExists] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();

  const checkUserDetails = async () => {
    if (!isLoaded) {
      return; // Don't do anything if Clerk hasn't loaded yet
    }
    
    if (isSignedIn && user) {
      setIsLoading(true);
      setFetchFailed(false);
      try {
        const token = await getToken();
  
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get_user_details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              // user_id: userId,
              email: user.primaryEmailAddress.emailAddress,
            }),
          }
        );

        if (response.status === 401) {
          setFetchFailed(true);
          toast.error("Login is not authenticated. Please sign out and try again.", {
            duration: 5000,
            icon: '🔒',
          });
          return;
        }

        if (!response.ok) {
          setFetchFailed(true);
          throw new Error("Failed to fetch user details");
        }

        const data = await response.json();
        sessionStorage.setItem("userDetails", JSON.stringify(data));
        setUserExists(!!data);
        setUserDetails(data);
      } catch (error) {
        console.error("Error checking user details:", error);
        setUserExists(false);
        setFetchFailed(true);
        toast.error("Unable to fetch user details");
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleRetryFetch = () => {
    setFetchFailed(false);
    setIsLoading(true);
    checkUserDetails();
  };

  useEffect(() => {
    checkUserDetails();
  }, [isLoaded, isSignedIn, user]);

  const value = {
    userDetails,
    userExists,
    isLoading,
    fetchFailed,
    checkUserDetails,
    handleRetryFetch,
    setUserExists,
    setUserDetails
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 